@file:Suppress("TooGenericExceptionCaught")

package blazern.lexisoup.core.audio

import arrow.core.Either
import blazern.lexisoup.core.logging.Log
import blazern.lexisoup.domain.error.Err
import kotlinx.browser.document
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import org.w3c.dom.CanPlayTypeResult
import org.w3c.dom.HTMLAudioElement
import org.w3c.dom.MAYBE
import org.w3c.dom.PROBABLY
import org.w3c.dom.events.Event

/**
 * NOTE: this class is entirely generated by ChatGPT.
 */
internal class JsAudioPlayer(
    urls: List<String>,
) : AudioPlayer {

    private var released = false

    private val _state = MutableStateFlow<AudioPlayer.State>(AudioPlayer.State.NotPlaying())
    override val state = _state.asStateFlow()

    private val audio: HTMLAudioElement =
        (document.createElement("audio") as HTMLAudioElement).apply {
            src = preferredUrl(urls)
            preload = "none"

            onplay = { _: Event ->
                _state.value = AudioPlayer.State.Playing()
            }

            onpause = { _: Event ->
                _state.value = AudioPlayer.State.NotPlaying()
            }

            onended = { _: Event ->
                _state.value = AudioPlayer.State.NotPlaying()
            }

            onerror = { _, _, _, _, _ ->
                Log.e(TAG) { "Error: ${audio.error}" }
                _state.value = AudioPlayer.State.NotPlaying()
            }
        }

    override suspend fun play(): Either<Err, Unit> {
        if (released) {
            return Either.Left(Err.from(IllegalStateException("play: the player was released")))
        }

        return try {
            audio.play().await()
            _state.value = AudioPlayer.State.Playing()
            Either.Right(Unit)
        } catch (e: Exception) {
            _state.value = AudioPlayer.State.NotPlaying()
            Either.Left(Err.from(e))
        }
    }

    override suspend fun reset(): Either<Err, Unit> {
        if (released) {
            return Either.Left(Err.from(IllegalStateException("reset: the player was released")))
        }

        return try {
            audio.pause()
            audio.currentTime = 0.0
            _state.value = AudioPlayer.State.NotPlaying()
            Either.Right(Unit)
        } catch (e: Exception) {
            Either.Left(Err.from(e))
        }
    }

    override fun release() {
        if (released) return
        released = true

        _state.value = AudioPlayer.State.NotPlaying()

        audio.pause()

        audio.onplay = null
        audio.onpause = null
        audio.onended = null
        audio.onerror = null

        // Helps the browser drop the resource.
        audio.removeAttribute("src")
        audio.load()
    }

    private fun preferredUrl(urls: List<String>): String {
        val probe = document.createElement("audio") as HTMLAudioElement

        fun supportScore(url: String): Int {
            val lower = url.lowercase()

            return when {
                lower.endsWith(".ogg") || lower.endsWith(".oga") ->
                    canPlayScore(probe.canPlayType("audio/ogg"))
                lower.endsWith(".mp3") ->
                    canPlayScore(probe.canPlayType("audio/mpeg"))
                lower.endsWith(".wav") ->
                    canPlayScore(probe.canPlayType("audio/wav"))
                lower.endsWith(".m4a") || lower.endsWith(".aac") ->
                    canPlayScore(probe.canPlayType("audio/mp4"))
                else -> 0
            }
        }

        return urls.maxByOrNull(::supportScore) ?: urls.first()
    }

    private fun canPlayScore(value: CanPlayTypeResult): Int =
        when (value) {
            CanPlayTypeResult.PROBABLY -> 2
            CanPlayTypeResult.MAYBE -> 1
            else -> 0
        }

    private companion object {
        const val TAG = "JsAudioPlayer"
    }
}
